import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { markTimeDiffStale } from '@modules/ServiceModule/workspace/lib/clientServerTimeDiff';
import { WebphoneCallState } from '@modules/SoftPhoneModule/types';
import { ITask } from '@modules/TasksModule/types';
import { IndexDbTable } from './lib/IndexDbTable';
import { UserActionEventTable } from './lib/UserActionEventTable';
import handleAgentHoldEnded from './hooks/handleAgentHoldEnded';
import handleAgentHoldStarted from './hooks/handleAgentHoldStarted';
import handleAgentRejected from './hooks/handleAgentRejected';

export type { UserActionEvent } from './lib/types';
export {
  IndexDbTable,
  type IndexDbTableConfig,
  type IndexDbTableDeleteEvent,
  type IndexDbTableEventMap,
  type IndexDbTableEventType,
  type IndexDbTableInitFailedEvent,
  type IndexDbTableRecordEvent,
} from './lib/IndexDbTable';

const DEFAULT_REFRESH_INTERVAL_MINUTES = 15;
const DEFAULT_FLUSH_INTERVAL_SECONDS = 2;

/**
 * IndexDbModule
 *
 * Central gateway for all IndexedDB access in the app.
 *
 * Each supported table is exposed as a typed public member.  To add a new
 * table: create a subclass of IndexDbTable, add it as a public member here,
 * and include it in the `tables` getter.
 *
 * ## Event capture
 * This module subscribes to softphone hold/unhold events and task reject
 * events, writing a timestamped UserActionEvent to `userActionEvents` on each
 * action.
 *
 * Timestamps are corrected for client/server clock skew:
 *   `correctedTs = Date.now() - clientServerTimeDiff.diffMs`
 *
 * The clientServerTimeDiff is refreshed every N minutes (default 15), controlled
 * by `feature-client-server-time-diff-refresh-interval-minutes`. The diff is marked
 * stale (not cleared) so the existing value remains available until the workspace's
 * RTCS event handler recomputes it on the next event — no gap in coverage.
 *
 * ## Module event contract
 * All module events carry the table identifier in their payload — never in
 * the event name — so the event surface stays stable as new tables are added:
 *
 *   'indexeddb.table.initialized'    { tableId: string }
 *   'indexeddb.table.init-failed'    { tableId: string; error: Error }
 *
 * ## Lifecycle
 * `preload()` — subscribes to each table's `initialized` and `init-failed` events,
 *               and registers user-action capture handlers.
 * `load()`    — calls `init()` on every table; results surface via the
 *               handlers registered in `preload()`.
 */
export class IndexDbModule extends AppModule {
  public override events = [
    'indexeddb.table.initialized',
    'indexeddb.table.init-failed',
  ];

  // ── Public table members ────────────────────────────────────────────────
  // Add new tables here as the schema grows.
  public readonly userActionEvents = new UserActionEventTable();

  // ── Internal helpers ────────────────────────────────────────────────────

  private timeDiffRefreshIntervalId: ReturnType<typeof setInterval> | null =
    null;

  private get tables(): IndexDbTable[] {
    return [this.userActionEvents];
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  /** Wires table lifecycle events to module-level broadcasts. */
  public override preload(): void {
    this.subscribeTableEvents();
    this.subscribeActionCapture();
    this.subscribeTimeDiffRefresh();
    this.subscribeFlushIntervalConfig();
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  private subscribeTableEvents(): void {
    for (const table of this.tables) {
      table.on('initialized', () => {
        this.app.logger.info(`IndexDbModule: table '${table.tableId}' ready`);
        this.app.events.emit('indexeddb.table.initialized', {
          tableId: table.tableId,
        });
      });
      table.on('init-failed', ({ error }) => {
        this.app.logger.error(
          `IndexDbModule: table '${table.tableId}' failed to initialize`,
          error,
        );
        this.app.events.emit('indexeddb.table.init-failed', {
          tableId: table.tableId,
          error,
        });
      });
    }
  }

  private subscribeActionCapture(): void {
    this.app.events.on(
      'phone.call.hold.started',
      (callState: WebphoneCallState) => {
        void handleAgentHoldStarted(
          callState,
          this.userActionEvents,
          this.app.logger,
        );
      },
    );

    this.app.events.on(
      'phone.call.hold.ended',
      (callState: WebphoneCallState) => {
        void handleAgentHoldEnded(
          callState,
          this.userActionEvents,
          this.app.logger,
        );
      },
    );

    this.app.events.on('task.action.reject', (task: ITask) => {
      void handleAgentRejected(task, this.userActionEvents, this.app.logger);
    });
  }

  // Marks clientServerTimeDiff stale on a periodic interval once feature flags
  // are ready. Stale triggers recomputation on the next RTCS event while keeping
  // the existing value available until then (no coverage gap).
  private subscribeTimeDiffRefresh(): void {
    this.app.events.on('featureflags.initialized', () => {
      const { value: intervalMinutes } =
        FeatureFlagModule.getFeatureFlagValue<number>(
          FeatureFlagKeys.featureClientServerTimeDiffRefreshIntervalMinutes,
        );

      const minutes =
        typeof intervalMinutes === 'number' && intervalMinutes > 0
          ? intervalMinutes
          : DEFAULT_REFRESH_INTERVAL_MINUTES;

      if (this.timeDiffRefreshIntervalId !== null) return;
      this.timeDiffRefreshIntervalId = setInterval(
        () => {
          markTimeDiffStale();
        },
        minutes * 60 * 1000,
      );
    });
  }

  private subscribeFlushIntervalConfig(): void {
    this.app.events.on('featureflags.initialized', () => {
      const { value: intervalSeconds } =
        FeatureFlagModule.getFeatureFlagValue<number>(
          FeatureFlagKeys.featureUserActionEventBatchIntervalSeconds,
        );

      const flushMs =
        typeof intervalSeconds === 'number' && intervalSeconds > 0
          ? intervalSeconds * 1000
          : DEFAULT_FLUSH_INTERVAL_SECONDS * 1000;

      this.userActionEvents.setFlushInterval(flushMs);
    });
  }

  /** Triggers IndexedDB initialisation for every table. */
  public override load(): void {
    for (const table of this.tables) {
      table.init().catch(() => {
        // Failures are surfaced via the 'init-failed' event registered in preload().
      });
    }
  }

  /** For test teardown only — modules are singletons and are never unloaded in production. */
  destroy(): void {
    if (this.timeDiffRefreshIntervalId !== null) {
      clearInterval(this.timeDiffRefreshIntervalId);
      this.timeDiffRefreshIntervalId = null;
    }
    this.userActionEvents.destroy();
  }
}

export default new IndexDbModule(
  appManager.events,
  appManager.logger('IndexDb'),
);
